import React, { useState } from 'react';
import theme from '../../theme/globalTheme';
import { Card, Upload, Alert, Typography, List, Tag, Divider, Button, Tabs } from 'antd';
import { UploadOutlined, CheckCircleOutlined, CloseCircleOutlined, WarningOutlined, DownloadOutlined, ToolOutlined } from '@ant-design/icons';
import PizZip from 'pizzip';
import { saveAs } from 'file-saver';

const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;

function LunchDocumentValidator() {
  const [validationResults, setValidationResults] = useState({
    contrat: null,
    confirmation: null,
    facture: null
  });
  const [loading, setLoading] = useState(false);
  const [originalZips, setOriginalZips] = useState({});
  const [originalFileNames, setOriginalFileNames] = useState({});

  // Variables pour chaque type de document Lunch
  const lunchVariables = {
    contrat: [
      'menu_text',
      'menu_price',
      'additional_text',
      'additional_price',
      'total_price',
      'client_name',
      'charter_date',
      'today_date'
    ],
    confirmation: [
      'menu_text',
      'menu_price',
      'additional_text',
      'additional_price',
      'total_price',
      'client_name',
      'charter_date',
      'today_date'
    ],
    facture: [
      'menu_text',
      'menu_price',
      'additional_text',
      'additional_price',
      'total_price',
      'client_name',
      'charter_date',
      'today_date',
      'invoice_number'
    ]
  };

  const debugDocumentContent = (documentXml) => {
    // Chercher tous les textes entre balises w:t
    const textRegex = /<w:t[^>]*>([^<]+)<\/w:t>/g;
    const texts = [];
    let textMatch;

    while ((textMatch = textRegex.exec(documentXml)) !== null) {
      texts.push(textMatch[1]);
    }

    console.log('📝 Textes trouvés dans le document:', texts);
    return texts;
  };

  const validateTemplate = async (file, documentType) => {
    setLoading(true);

    try {
      // Lire le fichier Word
      const arrayBuffer = await file.arrayBuffer();
      const zip = new PizZip(arrayBuffer);

      // Vérifier que le fichier est bien un DOCX valide
      const files = zip.file(/.*/);
      console.log('📁 Fichiers dans le ZIP:', files.map(f => f.name));

      // Vérifier que document.xml existe
      if (!zip.file('word/document.xml')) {
        throw new Error('Fichier DOCX invalide - document.xml manquant');
      }

      // Extraire le contenu XML du document
      const documentXml = zip.file('word/document.xml').asText();

      // Debug: afficher tous les textes
      const allTexts = debugDocumentContent(documentXml);

      // Variables attendues pour ce type de document
      const expectedVariables = lunchVariables[documentType] || [];

      // Rechercher les variables manquantes et présentes
      const foundVariables = [];
      const missingVariables = [];
      const malformedVariables = [];

      expectedVariables.forEach(varName => {
        // Chercher {{varName}} - Format lunch
        const correctPattern = `{{${varName}}}`;
        const malformedPattern1 = `{${varName}}`;
        const malformedPattern2 = `\${${varName}}`;

        if (documentXml.includes(correctPattern)) {
          foundVariables.push(varName);
        } else {
          missingVariables.push(varName);

          // Vérifier si la variable est mal formatée
          if (documentXml.includes(malformedPattern1) || documentXml.includes(malformedPattern2)) {
            malformedVariables.push(varName);
          }
        }
      });

      // Chercher toutes les variables dans le document (format {{variable}})
      const variablePattern = /\{\{([^}]+)\}\}/g;
      const allFoundVariables = [];
      let match;
      while ((match = variablePattern.exec(documentXml)) !== null) {
        if (!allFoundVariables.includes(match[1])) {
          allFoundVariables.push(match[1]);
        }
      }

      // Variables inconnues (présentes mais non attendues)
      const unknownVariables = allFoundVariables.filter(v => !expectedVariables.includes(v));

      // Stocker le ZIP et le nom original pour correction
      setOriginalZips(prev => ({ ...prev, [documentType]: zip }));
      setOriginalFileNames(prev => ({ ...prev, [documentType]: file.name }));

      // Résultats de validation
      const result = {
        fileName: file.name,
        isValid: missingVariables.length === 0 && unknownVariables.length === 0,
        foundVariables,
        missingVariables,
        malformedVariables,
        unknownVariables,
        allTexts
      };

      setValidationResults(prev => ({
        ...prev,
        [documentType]: result
      }));

    } catch (error) {
      console.error('Erreur lors de la validation:', error);
      setValidationResults(prev => ({
        ...prev,
        [documentType]: {
          error: error.message,
          isValid: false
        }
      }));
    } finally {
      setLoading(false);
    }

    return false;
  };

  const fixTemplate = (documentType) => {
    const zip = originalZips[documentType];
    const result = validationResults[documentType];

    if (!zip || !result) return;

    try {
      // Lire le document.xml
      let documentXml = zip.file('word/document.xml').asText();

      // Corriger les variables mal formatées vers le format {{variable}}
      result.malformedVariables.forEach(varName => {
        const malformedPattern1 = new RegExp(`(?<!{){${varName}}(?!})`, 'g');
        const malformedPattern2 = new RegExp(`\\$\\{${varName}\\}`, 'g');
        const correctPattern = `{{${varName}}}`;

        documentXml = documentXml.replace(malformedPattern1, correctPattern);
        documentXml = documentXml.replace(malformedPattern2, correctPattern);
      });

      // Mettre à jour le document dans le ZIP
      zip.file('word/document.xml', documentXml);

      // Générer le nouveau fichier
      const blob = zip.generate({ type: 'blob' });

      // Télécharger le fichier corrigé
      const fileName = originalFileNames[documentType] || 'document-corrected.docx';
      const newFileName = fileName.replace('.docx', '-fixed.docx');

      saveAs(blob, newFileName);

      alert(`✅ Template corrigé téléchargé: ${newFileName}`);
    } catch (error) {
      console.error('Erreur lors de la correction:', error);
      alert(`❌ Erreur: ${error.message}`);
    }
  };

  const renderValidationResult = (documentType, label) => {
    const result = validationResults[documentType];

    if (!result) return null;

    if (result.error) {
      return (
        <Alert
          type="error"
          message={`Erreur: ${result.error}`}
          style={{ marginTop: '16px' }}
        />
      );
    }

    const expectedVars = lunchVariables[documentType];

    return (
      <Card style={{ marginTop: '16px' }}>
        <Title level={5}>
          {result.isValid ? (
            <><CheckCircleOutlined style={{ color: theme.colors.white }} /> Template valide ✅</>
          ) : (
            <><CloseCircleOutlined style={{ color: theme.colors.textSecondary }} /> Template invalide ❌</>
          )}
        </Title>

        <Paragraph>
          <Text strong>Fichier:</Text> {result.fileName}
        </Paragraph>

        <Divider />

        <Title level={6}>Variables attendues ({expectedVars.length}):</Title>
        <List
          size="small"
          dataSource={expectedVars}
          renderItem={item => (
            <List.Item>
              <Tag color={result.foundVariables.includes(item) ? 'green' : 'red'}>
                {result.foundVariables.includes(item) ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
              </Tag>
              <code>{'{{' + item + '}}'}</code>
              {result.malformedVariables.includes(item) && (
                <Tag color="orange" style={{ marginLeft: '8px' }}>
                  <WarningOutlined /> Mal formatée
                </Tag>
              )}
            </List.Item>
          )}
        />

        {result.missingVariables.length > 0 && (
          <>
            <Divider />
            <Alert
              type="error"
              message={`Variables manquantes (${result.missingVariables.length})`}
              description={
                <List
                  size="small"
                  dataSource={result.missingVariables}
                  renderItem={item => (
                    <List.Item>
                      <code>{'{{' + item + '}}'}</code>
                    </List.Item>
                  )}
                />
              }
            />
          </>
        )}

        {result.unknownVariables.length > 0 && (
          <>
            <Divider />
            <Alert
              type="warning"
              message={`Variables non reconnues (${result.unknownVariables.length})`}
              description={
                <List
                  size="small"
                  dataSource={result.unknownVariables}
                  renderItem={item => (
                    <List.Item>
                      <code>{'{{' + item + '}}'}</code>
                    </List.Item>
                  )}
                />
              }
            />
          </>
        )}

        {result.malformedVariables.length > 0 && (
          <>
            <Divider />
            <Button
              type="primary"
              icon={<ToolOutlined />}
              onClick={() => fixTemplate(documentType)}
              style={{ marginTop: '16px' }}
            >
              Télécharger le template corrigé
            </Button>
          </>
        )}
      </Card>
    );
  };

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      <Card>
        <Title level={3}>🍽️ Validateur de Templates Lunch</Title>
        <Paragraph>
          Validez vos templates DOCX pour les contrats, confirmations et factures lunch.
          Les variables doivent être au format <code>{'{{'}</code>variable<code>{'}}'}</code>.
        </Paragraph>

        <Alert
          type="info"
          message="Variables attendues par document"
          description={
            <div>
              <p><strong>Contrat Lunch:</strong> menu_text, menu_price, additional_text, additional_price, total_price, client_name, charter_date, today_date</p>
              <p><strong>Confirmation Lunch:</strong> menu_text, menu_price, additional_text, additional_price, total_price, client_name, charter_date, today_date</p>
              <p><strong>Facture Lunch:</strong> menu_text, menu_price, additional_text, additional_price, total_price, client_name, charter_date, today_date, invoice_number</p>
            </div>
          }
          style={{ marginTop: '16px' }}
        />

        <Divider />

        <Tabs defaultActiveKey="contrat">
          <TabPane tab="📄 Contrat" key="contrat">
            <Upload
              beforeUpload={(file) => validateTemplate(file, 'contrat')}
              accept=".docx"
              showUploadList={false}
            >
              <Button icon={<UploadOutlined />} loading={loading}>
                Uploader contrat-lunch.docx
              </Button>
            </Upload>
            {renderValidationResult('contrat', 'Contrat Lunch')}
          </TabPane>

          <TabPane tab="✅ Confirmation" key="confirmation">
            <Upload
              beforeUpload={(file) => validateTemplate(file, 'confirmation')}
              accept=".docx"
              showUploadList={false}
            >
              <Button icon={<UploadOutlined />} loading={loading}>
                Uploader confirmation-lunch.docx
              </Button>
            </Upload>
            {renderValidationResult('confirmation', 'Confirmation Lunch')}
          </TabPane>

          <TabPane tab="💰 Facture" key="facture">
            <Upload
              beforeUpload={(file) => validateTemplate(file, 'facture')}
              accept=".docx"
              showUploadList={false}
            >
              <Button icon={<UploadOutlined />} loading={loading}>
                Uploader facture-lunch.docx
              </Button>
            </Upload>
            {renderValidationResult('facture', 'Facture Lunch')}
          </TabPane>
        </Tabs>

        <Divider />

        <Alert
          type="success"
          message="Comment utiliser le validateur"
          description={
            <ol>
              <li>Sélectionnez l'onglet du type de document (Contrat, Confirmation ou Facture)</li>
              <li>Uploadez votre template DOCX</li>
              <li>Vérifiez les résultats de validation</li>
              <li>Si des variables sont mal formatées, téléchargez le template corrigé</li>
              <li>Uploadez le template corrigé dans Supabase Storage (bucket: templates)</li>
            </ol>
          }
        />
      </Card>
    </div>
  );
}

export default LunchDocumentValidator;
